import { v2Request } from './client';

/** @param {'today' | 'tomorrow'} day */
export function deliveryDayToYyyyMmDd(day) {
  const d = new Date();
  if (day === 'tomorrow') d.setDate(d.getDate() + 1);
  return d.toISOString().split('T')[0];
}

/**
 * Pilot time slots for a store and calendar day.
 * @param {{ storeId: string, deliveryDate: string, podId?: string }} params - deliveryDate YYYY-MM-DD
 */
export async function getCheckoutTimeSlots({ storeId, deliveryDate, podId }) {
  const qs = new URLSearchParams({ storeId: String(storeId), deliveryDate: String(deliveryDate) });
  if (podId) qs.set('podId', String(podId).trim());
  const data = await v2Request(`/api/time-slots?${qs.toString()}`);
  return Array.isArray(data.timeSlots) ? data.timeSlots : [];
}

/** @returns {Record<string, boolean>} SKU upper -> in stock */
export async function getBranchStockMap(storeId) {
  const data = await v2Request(`/api/stock/${encodeURIComponent(String(storeId))}`);
  const raw = data?.data;
  if (!raw || typeof raw !== 'object') return {};
  return raw;
}

/** Cart lines whose variant SKU is missing or not true in branch stock map (keys uppercased). */
export function findOosCartLines(stockMap, products) {
  const list = [];
  if (!Array.isArray(products)) return list;
  for (const line of products) {
    const sku = String(line?.variant?.sku ?? '').trim().toUpperCase();
    if (!sku) continue;
    if (!stockMap[sku]) {
      list.push({ productId: line.productId, name: line.name || 'Item', sku });
    }
  }
  return list;
}

/**
 * Home-delivery fee quote (legacy ccc-server rules via Pilot /api/delivery/quote).
 * @param {{ storeBranchId: string, customerLat: number, customerLng: number, cartTotal?: number, deliveryType?: string }} params
 * @returns {Promise<{ deliveryCharge: number, distanceKm: number|null, freeDeliveryApplied: boolean, freeDeliveryAbove: number }>}
 */
export async function getDeliveryQuote({
  storeBranchId,
  customerLat,
  customerLng,
  cartTotal = 0,
  deliveryType = 'delivery',
}) {
  const subtotal = Math.max(0, Number(cartTotal) || 0);
  const raw = await v2Request('/api/delivery/quote', {
    method: 'POST',
    body: {
      storeBranchId: String(storeBranchId),
      customerLat: Number(customerLat),
      customerLng: Number(customerLng),
      cartTotal: subtotal,
      deliveryType,
    },
  });

  const charge =
    typeof raw.deliveryCharge === 'object' && raw.deliveryCharge != null
      ? Number(raw.deliveryCharge.charge)
      : Number(raw.deliveryCharge);

  return {
    deliveryCharge: Number.isFinite(charge) ? charge : 0,
    distanceKm: raw.distance ?? raw.deliveryCharge?.distance ?? null,
    freeDeliveryApplied: subtotal >= 999,
    freeDeliveryAbove: 999,
  };
}

/** V2 checkout payment channel for Pilot payloads. */
export const V2_PAYMENT_TYPE = {
  COD: 'cod',
  ONLINE: 'online',
};

/** @param {string} value */
export function isCodPaymentType(value) {
  return value === V2_PAYMENT_TYPE.COD;
}

/** @param {string} value */
export function isOnlinePaymentType(value) {
  return value === V2_PAYMENT_TYPE.ONLINE;
}
